import { SearchIcon } from 'lucide-react'
import { Suspense, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'

import { DesktopNavigation, MobileNavigation } from '../components/Navigation'
import { Sheet } from '../components/Sheet'
import { Link } from '../lib/Link'
import { useRouter, useSearchParams } from '../lib/navigation'
import { fetcher } from '../lib/swr'
import PlanDetail from '../pg/PlanDetail'
import type { FullPlan } from '../pg/types'

function usePreviousValue<T>(value: T) {
  const [previous, setPrevious] = useState(value)
  useEffect(() => {
    if (value && value !== previous) setPrevious(value)
  }, [value])
  return previous
}

function planPortions(plan: FullPlan) {
  if (plan.portionsConsumed != null) return plan.portionsConsumed
  const portionsEat = plan.portionsEat ?? plan.recipe?.portionsEat ?? 2
  return (plan.multiplier ?? 1) * portionsEat
}

function historyStats(plans: FullPlan[]) {
  const count = plans.length
  if (count === 0) {
    return { mealsPerWeek: 0, avgPortions: 0 }
  }

  const times = plans
    .map((p) => (p.consumedAt ? Date.parse(p.consumedAt) : NaN))
    .filter((t) => !Number.isNaN(t))
    .sort((a, b) => a - b)

  let mealsPerWeek = count
  if (times.length >= 2) {
    const spanMs = times[times.length - 1]! - times[0]!
    const weeks = Math.max(spanMs / (1000 * 60 * 60 * 24 * 7), 1 / 7)
    mealsPerWeek = count / weeks
  }

  const avgPortions = plans.reduce((sum, p) => sum + planPortions(p), 0) / count

  return { mealsPerWeek, avgPortions }
}

export default function Page() {
  const router = useRouter()
  const search = useSearchParams()
  const planId = Number(search.get('plan') || 0) || null

  const [limit, setLimit] = useState(50)
  const [term, setTerm] = useState('')
  const previousLimit = usePreviousValue(limit)
  const previous = useSWR<FullPlan[]>(
    `/api/plans?view=history&limit=${previousLimit}`,
    fetcher
  )
  const plans = useSWR<FullPlan[]>(
    limit ? `/api/plans?view=history&limit=${limit}` : null,
    fetcher
  )
  const recipes = useSWR<{ id: number }[]>('/api/recipes', fetcher)
  const list = previous.data || plans.data || []

  const filtered = useMemo(() => {
    const q = term.trim().toLowerCase()
    if (!q) return list
    return list.filter((plan) => {
      const hay = `${plan.display || ''} ${plan.recipe?.display || ''} ${plan.recipe?.main || ''}`
      return hay.toLowerCase().includes(q)
    })
  }, [list, term])

  const stats = useMemo(() => historyStats(list), [list])
  const recipeCount = recipes.data?.length ?? 0

  const selected = planId ? list.find((p) => p.id === planId) : undefined

  function closePlan() {
    router.replace('/history')
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Sheet
        open={Boolean(planId)}
        onClose={closePlan}
        wide
        sheet={
          planId ? (
            <PlanDetail
              planId={planId}
              plan={selected}
              onChange={() => {
                plans.mutate()
                previous.mutate()
              }}
              onClose={closePlan}
            />
          ) : null
        }
      >
        <main className="bg-bg text-white min-h-screen flex flex-col">
          <DesktopNavigation />

          <div className="flex-1 w-full max-w-xl lg:max-w-3xl mx-auto p-4 lg:p-6 pb-28 md:pb-6">
            <div className="lg:grid lg:grid-cols-[minmax(0,36rem)_9rem] lg:gap-16 lg:items-start">
              <aside className="my-4 lg:mb-0 lg:order-2 lg:sticky lg:top-24">
                <dl className="grid grid-cols-3 gap-2 lg:grid-cols-1 lg:gap-8">
                  <Stat
                    label="plannen per week"
                    value={stats.mealsPerWeek.toLocaleString('nl-BE', {
                      maximumFractionDigits: 1,
                    })}
                  />
                  <Stat
                    label="porties per maaltijd"
                    value={stats.avgPortions.toLocaleString('nl-BE', {
                      maximumFractionDigits: 1,
                    })}
                  />
                  <Stat
                    label="recepten"
                    value={recipeCount.toLocaleString('nl-BE')}
                  />
                </dl>
              </aside>

              <div className="min-w-0 lg:order-1 lg:max-w-xl">
                <div className="sticky top-0 sticky-below-nav z-10 -mx-4 px-4 py-3 md:mx-0 md:px-0 bg-bg/90 backdrop-blur-md mb-4">
                  <label className="relative block">
                    <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/45 pointer-events-none" />
                    <input
                      type="search"
                      placeholder="Zoeken in geschiedenis…"
                      value={term}
                      onChange={(e) => setTerm(e.target.value)}
                      className="w-full rounded-2xl bg-white/10 hover:bg-white/15 focus:bg-white/15 border border-white/10 focus:border-white/25 pl-12 pr-4 py-3.5 text-white placeholder:text-white/40 outline-none transition-colors"
                    />
                  </label>
                </div>

                <div className="flex flex-col -mx-4">
                  {filtered.map((plan) => (
                    <Link
                      href={`/history?plan=${plan.id}`}
                      key={plan.id}
                      className="flex gap-4 items-center group focus:outline-hidden hover:bg-white/10 rounded-lg px-4 py-2 transition-colors"
                    >
                      <h2 className="flex-1 font-medium leading-none text-base lg:text-xl group-hover:underline group-focus-visible:underline opacity-80 group-hover:opacity-100 group-focus-visible:opacity-100">
                        {plan.multiplier && plan.multiplier !== 1 && (
                          <span className="opacity-60">{plan.multiplier}x</span>
                        )}{' '}
                        {plan.display || plan.recipe?.display}
                      </h2>
                      <div className="opacity-60 text-xs lg:text-sm shrink-0">
                        {plan.consumedAt &&
                          new Date(plan.consumedAt)?.toLocaleDateString(
                            'nl-BE',
                            {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            }
                          )}
                      </div>
                    </Link>
                  ))}
                  {filtered.length === 0 && list.length > 0 ? (
                    <p className="px-4 py-6 text-white/45">Geen resultaten</p>
                  ) : null}
                </div>

                {limit <= list.length && (
                  <button
                    type="button"
                    onClick={() => setLimit(limit * 3)}
                    className="mt-6 mb-12 block w-full bg-white/10 rounded p-4 lg:p-6 text-center hover:bg-white hover:text-bg focus-visible:bg-white focus-visible:text-bg outline-hidden"
                  >
                    Load more history
                  </button>
                )}
              </div>
            </div>
          </div>

          <MobileNavigation />
        </main>
      </Sheet>
    </Suspense>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dd className="text-3xl lg:text-3xl font-semibold tabular-nums tracking-tight">
        {value}
      </dd>
      <dt className="text-sm lg:text-sm text-white/45 mb-0.5 lg:mb-1">
        {label}
      </dt>
    </div>
  )
}
